import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiRequest } from '../../../utils/fetch';
import { ApiTokenInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialToken: Partial<ApiTokenInterface> = {
  userId: 0,
  active: true,
  validUntil: new Date(),
};

interface ApiTokenResponse extends ApiTokenInterface {
  apiToken: string;
}

const AdSpaceApiTokensAdminAddApiTokenPage: React.FC = () => {
  const [token, setToken] = useState<Partial<ApiTokenInterface>>(initialToken);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  const handleChange = (field: keyof ApiTokenInterface, value: any) => {
    setToken(a => ({ ...a, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const response = await apiRequest<GenericResponseInterface<ApiTokenResponse>>('/apiTokens/insert', 'POST', token);
      if (response.status && response.data) {
        setSuccess(response.message || 'API token created successfully!');
        setDialogOpen(true);
        setToken(initialToken);
        setGeneratedToken(response.data.apiToken || null); 
      } else {
        setError(response.message || 'Failed to create API token');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create API token');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New API Token</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="User ID"
          type="number"
          value={token.userId ?? ''}
          onChange={e => handleChange('userId', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={token.active !== undefined ? (token.active ? 'yes' : 'no') : 'yes'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Valid Until"
          type="datetime-local"
          value={token.validUntil ? new Date(token.validUntil).toISOString().slice(0, 16) : ''}
          onChange={e => handleChange('validUntil', new Date(e.target.value))}
          fullWidth
          margin="dense"
          required
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create API Token
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
          {generatedToken && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">API Token (copy now, you won't see it again):</Typography>
              <Paper sx={{ p: 1, mt: 1, wordBreak: 'break-all', bgcolor: '#f5f5f5' }}>
                <Typography variant="body2">{generatedToken}</Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdSpaceApiTokensAdminAddApiTokenPage;